import React from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Link } from '@mui/material';
import Logo from '../../components/LayoutComponents/Logo';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

const styles = {
  root: {
    flexGrow: 1,
    backgroundColor: 'white',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '2rem 1rem',
  },
  tabs: {
    borderBottom: `1px solid #ccc`,
    marginBottom: '1rem',
    width: '100%',
    maxWidth: '800px',
  },
  tabContent: {
    width: '100%',
    maxWidth: '800px',
    padding: '1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
};

export default function VerticalTabs() {
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={styles.root}>
      {/* Logo Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <Logo />
      </Box>

      {/* Tabs Section */}
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="Vertical tabs example"
        variant="scrollable"
        sx={styles.tabs}
      >
        <Tab label="Privacy Policy" {...a11yProps(0)} />
        <Tab label="Interpretation and Definitions" {...a11yProps(1)} />
        <Tab label="Collecting and Using Your Personal Data" {...a11yProps(2)} />
        <Tab label="Links to Other Websites" {...a11yProps(3)} />
        <Tab label="Changes to this Privacy Policy" {...a11yProps(4)} />
      </Tabs>

      {/* Tab Content */}
      <Box sx={styles.tabContent}>
        <TabPanel value={value} index={0}>
          <Typography variant="h6" paragraph>
            <b>Last updated: March 17, 2025</b>
          </Typography>
          <Typography variant="h6" paragraph>
            This Privacy Policy describes our policies and procedures on the collection, use, and disclosure of your information when you use the service and tells you about your privacy rights and how the law protects you. We use your personal data to provide and improve the service. By using the service, you agree to the collection and use of information in accordance with this Privacy Policy.
          </Typography>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Typography variant="h6" paragraph>
            <b><u>Interpretations & Definitions</u></b>
          </Typography>
          <Typography variant="h6" paragraph>
            <b>Interpretation</b>
            <br />
            The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
          </Typography>
          <Typography variant="h6" paragraph>
            <b><u>Definitions</u></b>
          </Typography>
          <Typography variant="h6" paragraph>
            For the purposes of this Privacy Policy:
            <ul>
              <li>“Account” means a unique account created for you to access our service or parts of our service.</li>
              <li>“Company” (referred to as either “the Company”, “We”, “Us” or “Our” in this Privacy Policy) refers to the National Institute for Industry and Career Advancement, Inc.</li>
              <li>“Cookies” are small files that are placed on your computer, mobile device, or any other device by a website, containing the details of your browsing history on that website among other things.</li>
              <li>“Device” means any device that can access the service such as a computer, a cellphone, or a digital tablet.</li>
              <li>“Personal Data” is any information that relates to an identified or identifiable individual.</li>
              <li>"Service” refers to the Website.</li>
              <li>“Service Provider” means the Company and any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the service, to provide the service on behalf of the Company, to perform services related to the service, or to assist the Company in analyzing how the service is used.</li>
              <li>“Third-party Social Media Service” refers to any website or any social network website through which a user can log in or create an account to use the service.</li>
              <li>“Usage Data” refers to data collected automatically, either generated by the use of the service or from the service infrastructure itself (for example, the duration of a page visit).</li>
              <li>“Website” refers to <Link target="_blank" rel="noreferrer" href="http://niica.org">http://niica.org</Link>.</li>
              <li>“You” means the individual accessing or using the service, or the company, or other legal entity on behalf of which such individual is accessing or using the service, as applicable.</li>
            </ul>
          </Typography>
        </TabPanel>

        <TabPanel value={value} index={2}>
          <Typography variant="h6" paragraph>
            <b>Types of Data Collected:</b>
          </Typography>
          <Typography variant="h6" paragraph>
            <b><u>Personal Data</u></b>
          </Typography>
          <Typography variant="h6" paragraph>
            While using our service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. Personally identifiable information may include, but is not limited to:
            <ul>
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Phone number</li>
              <li>Address, state, province, zip/postal code, city</li>
              <li>Usage data</li>
            </ul>
          </Typography>
        </TabPanel>

        <TabPanel value={value} index={3}>
          <Typography variant="h6" paragraph>
            Our service may contain links to other websites that are not operated by us. If you click on a third-party link, you will be directed to that third party’s site. We strongly advise you to review the Privacy Policy of every site you visit. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
          </Typography>
        </TabPanel>

        <TabPanel value={value} index={4}>
          <Typography variant="h6" paragraph>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
            <br />
            We will let you know via email and/or a prominent notice on our service, prior to the change becoming effective and update the “Last updated” date at the top of this Privacy Policy.
            <br />
            You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
          </Typography>
        </TabPanel>
      </Box>
    </Box>
  );
}